import { FSNode } from './fs-types';
import { CreateProjectOptions, GitWorkspaceInfo, OpenSaveDialogOptions, RecentProject } from './project-types';
import type { EmscriptenModule } from './emscripten-types';
import { AuthState } from './auth-types';
import { LicenseState } from './license-types';
import { LayoutMode } from './ui-types';

export type Result = {
    success: boolean;
    error?: string | null;
};

export type DirectoryData = {
    files: FSNode[];
    dirPath: string;
};

export type DirectoryRefreshPayload = DirectoryData | DirectoryData[] | FSNode[] | null | undefined;
export type DirectoryRefreshCallback = (event: unknown, files: DirectoryRefreshPayload) => void;

export type MessageOptions = {
    noNewline: boolean;
};

export type LogMessage = {
    status: string;
    message: string;
    options?: MessageOptions;
};

/** Transient toast/notification severity for `IPlatformApi.showNotification`. */
export enum MessageType {
    DEFAULT = 'default',
    ERROR = 'error',
    SUCCESS = 'success',
    WARNING = 'warning'
}

/** Alias for `MessageType` (platform API / gateway naming). */
export type NotificationType = MessageType;

export type ToolchainSetupProgressStatus = 'running' | 'success' | 'error' | 'hidden';

export type ToolchainSetupProgress = {
    isOpen: boolean;
    status: ToolchainSetupProgressStatus;
    title: string;
    stepIndex: number;
    totalSteps: number;
    stepName: string;
    message: string;
    stepProgress: number;
    error?: string;
};

// Menu types
export type SerializableMenuItem = {
    id?: string;
    label?: string;
    type?: 'normal' | 'separator' | 'submenu' | 'checkbox' | 'radio';
    enabled?: boolean;
    accelerator?: string;
    submenu?: SerializableMenuItem[];
    role?: string;
};

// The LVGLSchemaService needs only a subset of IPlatformApi
export type BasicPlatformApi = {
    getResourcesSourcePath(): string | Promise<string>;
    onFileSystemChange(
        callback: (event: unknown, fileEvent: string, filePath: string) => void,
        callbackId: symbol
    ): void;
    removeFileSystemChangeListener(callbackId: symbol): void;
    getDirectoryData(dirPath: string): Promise<DirectoryData | null>;
    getFileData(filePath: string, options?: { suppressError?: boolean }): Promise<string>;
};

export type WasmWorkerPlatformApi = {
    getFileData(filePath: string, options?: { suppressError?: boolean }): Promise<string>;
    getBinaryFileData(filePath: string, options?: { suppressError?: boolean }): Promise<Uint8Array>;
    writeBinaryFile(path: string, data: Uint8Array): Promise<void>;
    delete(path: string): Promise<void>;
};

// type AssertFSBrowserPlatformApiIsSubset<T extends Pick<IPlatformApi, keyof FSBrowserPlatformApi>> = T;
export type FSBrowserPlatformApi = {
    getFileData(filePath: string, options?: { suppressError?: boolean }): Promise<string>;
    saveFileData(filePath: string, content: string): Promise<boolean>;
    writeBinaryFile(filePath: string, data: Uint8Array): Promise<void>;
    getBinaryFileData(filePath: string, options?: { suppressError?: boolean }): Promise<Uint8Array>;
};

export type LicenseServicePlatformApi = BasicPlatformApi & {
    // Get the path to the resources (e.g. LVGL source code) bundled with the app.
    getResourcesPath(): Promise<string>;
};

export type IPlatformApi = LicenseServicePlatformApi &
    WasmWorkerPlatformApi & {
        devmode: boolean;
        type: 'theia';

        openTabContextMenu(filePath: string): void;
        runContextMenuAction(action: string, path: string, name?: string): Promise<Result>;
        openContextMenu(filePath: string): Promise<Result>;
        onLogMessage(callback: (event: unknown, status: LogMessage) => void): symbol;
        removeOnLogMessageListener(callbackId?: symbol): void;
        onLogMessageRemoveListener(callback: (event: unknown, status: LogMessage) => void): void;
        removeLogMessageRemoveListener(): void;
        onToolchainSetupProgress(callback: (event: unknown, progress: ToolchainSetupProgress) => void): symbol;
        removeToolchainSetupProgressListener(callbackId?: symbol): void;
        shortenPath(path: string): string;

        // File operations
        selectDirectory(projectPath?: string): Promise<DirectoryData | null>;
        /** Pick an existing directory without opening it as a project (e.g. create-project parent folder). */
        openDirectorySelector(defaultPath?: string): Promise<string>;
        openSaveDialog(options?: OpenSaveDialogOptions): Promise<string>;
        getDirectoryData(dirPath: string): Promise<DirectoryData | null>;
        pathIsDirectory(dirPath: string): Promise<boolean>;
        getFileData(filePath: string, options?: { suppressError?: boolean }): Promise<string>;
        getBinaryFileData(filePath: string, options?: { suppressError?: boolean }): Promise<Uint8Array>;
        saveFileData(filePath: string, content: string): Promise<boolean>;
        writeBinaryFile(path: string, data: Uint8Array): Promise<void>;
        delete(path: string): Promise<void>;

        // Project operations
        createProject(projectPath: string, options: CreateProjectOptions): Promise<boolean>;
        generateProjectCode(projectPath: string): Promise<Result>;
        compileProject(projectPath: string): Promise<Result>;
        clearUserDataCache(): Promise<Result>;
        cleanProject(projectPath: string): Promise<Result>;
        getGitRemotes(projectPath: string): Promise<GitWorkspaceInfo | undefined>;
        isGitInstalled(): Promise<boolean>;
        getRecentProjects(): Promise<RecentProject[]>;
        addRecentProject(projectPath: string, currentTabs: string[]): Promise<string | null>;
        removeRecentProject(projectPath: string): Promise<void>;
        getOpenedProject(): Promise<string | null>;
        saveOpenedProject(projectPath: string | null): Promise<void>;
        saveLayoutMode(layoutMode: LayoutMode): Promise<void>;
        getLayoutMode(): Promise<LayoutMode>;
        getCodeGenModes(): Promise<string[]>;
        getCodeGenMode(): Promise<string>;
        setCodeGenMode(mode: string): Promise<void>;
        copyComponentLibraryToProject(
            projectPath: string,
            resourcesSrcPath: string,
            lvglVersion: string,
            libraryName: string
        ): Promise<void>;

        // Event listeners
        onDirectorySelected(callback: (event: unknown, data: DirectoryData) => void): void;
        removeDirectorySelectedListener(): void;
        onRefreshDirectory(callback: DirectoryRefreshCallback): void;
        removeRefreshDirectoryListener(callback?: DirectoryRefreshCallback): void;
        onStartFNInteraction(
            callback: (event: unknown, action: string, filePath: string, fileName: string) => void
        ): void;
        removeStartFNInteraction(): void;
        onTabContextMenuAction(callback: (event: unknown, action: string, filePath: string) => void): void;
        removeTabContextMenuActionListener(): void;
        onRecentProjects(callback: (event: unknown, projects: RecentProject[]) => void): void;
        removeRecentProjectsListener(): void;

        onFileSystemChange(
            callback: (event: unknown, fileEvent: string, filePath: string) => void,
            callbackId: symbol
        ): void;
        removeFileSystemChangeListener(callbackId: symbol): void;
        onAppCloseRequest(callback: () => void): void;
        removeAppCloseRequestListener(): void;
        sendAppCloseResponse(): void;
        showErrorMessageBox(title: string, message: string): Promise<number>;
        showUnsavedFilesDialog(title: string, message: string): Promise<number>;
        showConfirmDialog(
            title: string,
            message: string,
            confirmLabel?: string,
            cancelLabel?: string
        ): Promise<boolean>;
        /** Transient toast/notification (Theia MessageService, etc.). */
        showNotification(message: string, type?: MessageType): void;
        updateMenu(): Promise<void>;
        openExternal(url: string): Promise<void>;

        // Menu operations
        getMenuStructure(): Promise<SerializableMenuItem[]>;
        handleMenuItemClick(menuId: string): Promise<void>;

        // Global event system
        sendGlobalEvent(eventId: string): void;
        onGlobalEvent(callback: (eventId: string) => void, callbackId: symbol): void;
        removeGlobalEventListener(callbackId: symbol): void;

        /** Runs a host command when supported (Theia). */
        executeCommand?(commandId: string): Promise<void>;

        // Auth/license state persistence
        saveAuthState(authState: Partial<AuthState>): Promise<void>;
        getAuthState(): Promise<AuthState>;
        clearAuthState(): Promise<void>;
        saveLicenseState(licenseState: Partial<LicenseState>): Promise<void>;
        getLicenseState(): Promise<LicenseState>;
        clearLicenseState(): Promise<void>;
    };

type IEclipseApi = {
    openDirectoryDialog: () => string | null;
};

declare global {
    interface Window {
        createModule: (moduleInstance: Partial<EmscriptenModule>) => Promise<Partial<EmscriptenModule>>;
        Module: Partial<EmscriptenModule>;
        eclipse?: IEclipseApi;
        platform: {
            getHostPlatform: () => string;
        };
        _platformApi?: IPlatformApi;
        moduleInstance_canvas?: EmscriptenModule;
        PROJECT_ROOT_PATH: string;
        MANIFEST_PATH: string;
        LVGL_EDITOR_SCHEME: string;
        LVGL_EDITOR_VERSION: string;
        LVGL_THEIA_BROWSER_ONLY?: boolean;
        __lvedLicenseInitDone?: boolean;
        __lvedLegalInitDone?: boolean;
        MonacoEnvironment?: {
            getWorker: () => Worker;
        };
    }
}
